import { parseBahtToSatang } from '@pos/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';

import { Btn } from '../../components/Btn';
import { Card } from '../../components/Card';
import { PageHeader } from '../../components/PageHeader';
import { PromoPreviewCard } from '../../components/PromoPreviewCard';
import { listBranches } from '../../data/fetchers';
import { upsertPromotion } from '../../data/mutators';
import { canEditPromotions } from '../../lib/permissions';
import { useSession } from '../../stores/session';
import type { Promotion } from '../../types/domain';
import { PromotionProductSelector } from './PromotionProductSelector';

const blank = (): Promotion => ({
  id: '',
  name: '',
  type: 'percent',
  value: 0,
  status: 'scheduled',
  startDate: '',
  endDate: '',
  scope: 'all',
  branchIds: [],
  productIds: [],
});

const inputCls =
  'h-10 px-3 rounded-btn border border-line bg-surface text-body disabled:bg-bg-warm focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10 transition-all duration-200';

export function PromotionFormView({
  promo,
  onClose,
}: {
  promo: Promotion | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const user = useSession((s) => s.user);
  const canEdit = user ? canEditPromotions(user) : false;
  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });
  const [form, setForm] = useState<Promotion>(blank());
  const requiresProducts = form.type === 'bogo' || form.type === 'qty_tier';
  const tierInvalid =
    form.type === 'qty_tier' &&
    (!(form.minQty && form.minQty >= 1) || form.value <= 0);

  useEffect(() => {
    setForm(promo ? { ...promo } : blank());
  }, [promo]);

  const mutation = useMutation({
    mutationFn: upsertPromotion,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['promotions'] });
      onClose();
    },
  });

  const set = <K extends keyof Promotion>(k: K, v: Promotion[K]) =>
    setForm((f) => ({ ...f, [k]: v }));
  const toggleBranch = (id: string) =>
    setForm((f) => ({
      ...f,
      branchIds: f.branchIds.includes(id)
        ? f.branchIds.filter((x) => x !== id)
        : [...f.branchIds, id],
    }));
  const numOrUndef = (v: string) => (v === '' ? undefined : Number(v));

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (
      !canEdit ||
      !form.name.trim() ||
      (requiresProducts && (form.productIds?.length ?? 0) === 0) ||
      tierInvalid
    )
      return;
    mutation.mutate(form);
  };

  return (
    <div className="bo-fade max-w-5xl mx-auto w-full">
      <PageHeader
        title={promo ? 'แก้ไขโปรโมชั่น' : 'เพิ่มโปรโมชั่น'}
        subtitle={
          canEdit
            ? 'กำหนดรายละเอียดเงื่อนไขและประเภทส่วนลด'
            : 'อ่านอย่างเดียว (ไม่มีสิทธิ์แก้ไข)'
        }
      />

      <form
        onSubmit={onSubmit}
        className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start"
      >
        {/* Left Column: Form Details */}
        <div className="lg:col-span-2 space-y-4">
          {/* Card 1: Basic Info */}
          <Card title="ข้อมูลโปรโมชั่น">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="flex flex-col gap-1.5 sm:col-span-2">
                <span className="text-sm-label font-bold text-ink-2">
                  ชื่อโปรโมชั่น
                </span>
                <input
                  aria-label="ชื่อโปรโมชั่น"
                  disabled={!canEdit}
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  className={inputCls}
                  placeholder="เช่น ลด 10% ต้อนรับเปิดเทอม, ซื้อ 1 แถม 1 ชานมไข่มุก"
                  required
                />
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-sm-label font-bold text-ink-2">
                  ประเภทส่วนลด
                </span>
                <div className="relative">
                  <select
                    aria-label="ประเภท"
                    disabled={!canEdit}
                    value={form.type}
                    onChange={(e) => {
                      const nextType = e.target.value as Promotion['type'];
                      const prevType = form.type;
                      let nextValue = form.value;
                      // Reset value when type changes to prevent unit mismatch:
                      // baht↔percent would render stale value in wrong unit (5000% or 0.20 ฿)
                      if (nextType !== prevType) {
                        nextValue = nextType === 'bogo' ? 1 : 0;
                      }
                      setForm((f) => ({
                        ...f,
                        type: nextType,
                        value: nextValue,
                        minQty:
                          nextType === 'bogo'
                            ? 2
                            : nextType === 'qty_tier'
                              ? 1
                              : f.minQty,
                      }));
                    }}
                    className="w-full h-10 pl-3 pr-8 rounded-btn border border-line bg-surface text-body disabled:bg-bg-warm focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10 appearance-none cursor-pointer transition-all duration-200"
                  >
                    <option value="percent">ลดเป็นเปอร์เซ็นต์ (%)</option>
                    <option value="baht">ลดเป็นบาท (฿)</option>
                    <option value="bogo">ซื้อ 1 แถม 1 (BOGO)</option>
                    <option value="qty_tier">ลดตามจำนวนแบบทบชุด</option>
                  </select>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-2.5 pointer-events-none text-ink-3">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </div>
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-sm-label font-bold text-ink-2">
                  {form.type === 'qty_tier'
                    ? 'ส่วนลดต่อชุด (บาท)'
                    : 'มูลค่าที่ลด / ของแถม'}
                </span>
                <input
                  aria-label="มูลค่า"
                  disabled={!canEdit || form.type === 'bogo'}
                  type="number"
                  min={0}
                  step={
                    form.type === 'baht' || form.type === 'qty_tier'
                      ? '0.01'
                      : '1'
                  }
                  value={
                    form.type === 'baht' || form.type === 'qty_tier'
                      ? form.value / 100
                      : form.value
                  }
                  onChange={(e) =>
                    set(
                      'value',
                      form.type === 'baht' || form.type === 'qty_tier'
                        ? parseBahtToSatang(e.target.value)
                        : Number(e.target.value),
                    )
                  }
                  className={`${inputCls} num`}
                  placeholder={
                    form.type === 'bogo'
                      ? 'ซื้อ 1 แถม 1 (ไม่ต้องระบุมูลค่า)'
                      : form.type === 'qty_tier'
                        ? 'เช่น 50.00'
                        : form.type === 'baht'
                          ? 'เช่น 50.00'
                          : 'เช่น 10'
                  }
                  required
                />
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-sm-label font-bold text-ink-2">
                  สถานะแคมเปญ
                </span>
                <div className="relative">
                  <select
                    aria-label="สถานะ"
                    disabled={!canEdit}
                    value={form.status}
                    onChange={(e) =>
                      set('status', e.target.value as Promotion['status'])
                    }
                    className="w-full h-10 pl-3 pr-8 rounded-btn border border-line bg-surface text-body disabled:bg-bg-warm focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10 appearance-none cursor-pointer transition-all duration-200"
                  >
                    <option value="active">กำลังใช้งาน (Active)</option>
                    <option value="scheduled">
                      ตั้งเวลาเปิดอัตโนมัติ (Scheduled)
                    </option>
                    <option value="ended">สิ้นสุดแคมเปญ (Ended)</option>
                  </select>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-2.5 pointer-events-none text-ink-3">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </span>
                </div>
              </label>

              <div className="grid grid-cols-2 gap-3 sm:col-span-1">
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm-label font-bold text-ink-2">
                    วันที่เริ่ม
                  </span>
                  <input
                    aria-label="วันที่เริ่ม"
                    disabled={!canEdit}
                    type="date"
                    value={form.startDate}
                    onChange={(e) => set('startDate', e.target.value)}
                    className={inputCls}
                    required
                  />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm-label font-bold text-ink-2">
                    วันที่สิ้นสุด
                  </span>
                  <input
                    aria-label="วันที่สิ้นสุด"
                    disabled={!canEdit}
                    type="date"
                    value={form.endDate}
                    onChange={(e) => set('endDate', e.target.value)}
                    className={inputCls}
                    required
                  />
                </label>
              </div>
            </div>
          </Card>

          <Card title="สินค้าที่ร่วมรายการ">
            <div className="flex flex-col gap-3">
              {requiresProducts && (
                <p className="text-sm text-ink-3">
                  {form.type === 'qty_tier'
                    ? 'โปรลดตามจำนวนต้องเลือกสินค้าอย่างน้อย 1 รายการ จำนวนจะรวมจากสินค้าที่เลือกในบิล'
                    : 'โปรซื้อ 1 แถม 1 ต้องเลือกสินค้าอย่างน้อย 1 รายการ'}
                </p>
              )}
              <PromotionProductSelector
                selectedIds={form.productIds ?? []}
                disabled={!canEdit}
                onChange={(productIds) => set('productIds', productIds)}
              />
            </div>
          </Card>

          {/* Card 2: Scope of branches */}
          <Card title="ขอบเขตสาขาที่ร่วมรายการ">
            <div className="flex flex-col gap-3">
              <span className="text-sm-label font-bold text-ink-2">
                ขอบเขตการใช้งาน
              </span>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="radio"
                    name="scope"
                    disabled={!canEdit}
                    checked={form.scope === 'all'}
                    onChange={() => set('scope', 'all')}
                    className="w-4 h-4 text-primary focus:ring-primary/20 accent-primary cursor-pointer"
                  />
                  <span className="text-body font-semibold text-ink-2">
                    ร่วมรายการทุกสาขา
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="radio"
                    name="scope"
                    disabled={!canEdit}
                    checked={form.scope === 'specific'}
                    onChange={() => set('scope', 'specific')}
                    className="w-4 h-4 text-primary focus:ring-primary/20 accent-primary cursor-pointer"
                  />
                  <span className="text-body font-semibold text-ink-2">
                    ร่วมรายการเฉพาะบางสาขา
                  </span>
                </label>
              </div>

              {form.scope === 'specific' && (
                <div className="flex flex-wrap gap-4 pt-2 border-t border-line-2/50 mt-1">
                  {branches.map((b) => (
                    <label
                      key={b.id}
                      className="flex items-center gap-2 cursor-pointer select-none"
                    >
                      <input
                        type="checkbox"
                        disabled={!canEdit}
                        checked={form.branchIds.includes(b.id)}
                        onChange={() => toggleBranch(b.id)}
                        className="w-4.5 h-4.5 rounded border-line text-primary focus:ring-primary/30 accent-primary cursor-pointer"
                      />
                      <span className="text-body font-semibold text-ink-2">
                        {b.short}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </Card>

          {/* Card 3: Conditions */}
          <Card title="เงื่อนไขส่วนลด (เพิ่มเติม)">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <label className="flex flex-col gap-1.5">
                <span className="text-sm-label font-bold text-ink-2">
                  ยอดซื้อขั้นต่ำ (บาท)
                </span>
                <input
                  aria-label="ยอดขั้นต่ำ (บาท)"
                  disabled={!canEdit}
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.minBaht != null ? form.minBaht / 100 : ''}
                  onChange={(e) =>
                    set(
                      'minBaht',
                      e.target.value === ''
                        ? undefined
                        : parseBahtToSatang(e.target.value),
                    )
                  }
                  className={`${inputCls} num`}
                  placeholder="เช่น 100"
                />
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-sm-label font-bold text-ink-2">
                  {form.type === 'qty_tier'
                    ? 'จำนวนชิ้นต่อชุด'
                    : 'จำนวนสินค้าขั้นต่ำ (ชิ้น)'}
                </span>
                <input
                  aria-label="จำนวนขั้นต่ำ"
                  disabled={!canEdit || form.type === 'bogo'}
                  type="number"
                  min={form.type === 'qty_tier' ? 1 : 0}
                  value={form.minQty ?? ''}
                  onChange={(e) => set('minQty', numOrUndef(e.target.value))}
                  className={`${inputCls} num`}
                  placeholder={form.type === 'qty_tier' ? 'เช่น 24' : 'เช่น 2'}
                />
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-sm-label font-bold text-ink-2">
                  {form.type === 'qty_tier'
                    ? 'จำนวนชุดสูงสุดต่อบิล'
                    : 'จำกัดสิทธิ์ต่อบิล'}
                </span>
                <input
                  aria-label="จำกัดต่อบิล"
                  disabled={!canEdit}
                  type="number"
                  min={1}
                  value={form.perBillLimit ?? ''}
                  onChange={(e) =>
                    set('perBillLimit', numOrUndef(e.target.value))
                  }
                  className={`${inputCls} num`}
                  placeholder="เช่น 1"
                />
              </label>
            </div>
          </Card>

          {/* Footer Actions */}
          <div className="flex gap-2 pt-2">
            <Btn kind="default" type="button" onClick={onClose}>
              ยกเลิก
            </Btn>
            {canEdit && (
              <Btn
                kind="primary"
                type="submit"
                disabled={
                  (requiresProducts && (form.productIds?.length ?? 0) === 0) ||
                  tierInvalid
                }
              >
                บันทึก
              </Btn>
            )}
          </div>
        </div>

        {/* Right Column: Visual POS Preview */}
        <div className="lg:col-span-1 sticky top-6">
          <Card
            title="ตัวอย่างบนเครื่อง POS"
            className="shadow-md bg-surface/90 border border-line-2"
          >
            <div className="flex flex-col items-center gap-4 py-3">
              <p className="text-xs text-ink-3 text-center leading-relaxed">
                จำลองหน้าต่างโปรโมชั่นที่แสดงผลบน
                <br />
                หน้าจอฝั่งเครื่องแคชเชียร์ POS
              </p>

              {/* Promo Preview Card Container */}
              <div className="w-full bg-bg-warm border border-line-2 rounded-xl p-5 flex items-center justify-center min-h-[140px] shadow-inner">
                <PromoPreviewCard promo={form} />
              </div>

              <div className="text-[11px] text-ink-3 flex flex-col gap-1 w-full bg-bg/50 p-3 rounded-btn border border-line-2 select-none">
                <div className="flex justify-between">
                  <span>ขอบเขต:</span>
                  <span className="font-bold text-ink-2">
                    {form.scope === 'all'
                      ? 'ทุกสาขา'
                      : `เฉพาะสาขาที่กำหนด (${form.branchIds.length})`}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>สถานะ:</span>
                  <span
                    className={`font-bold ${form.status === 'active' ? 'text-ok' : form.status === 'scheduled' ? 'text-primary' : 'text-ink-4'}`}
                  >
                    {form.status === 'active'
                      ? 'เริ่มใช้งานทันที'
                      : form.status === 'scheduled'
                        ? 'ตั้งเวลารอกำหนด'
                        : 'แคมเปญสิ้นสุด'}
                  </span>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </form>
    </div>
  );
}
